import 'es6-promise/auto'
import axios from 'axios'
import Vue from 'vue'
import Vuex from 'vuex'
import api from '../api'
import createPersistedState from "vuex-persistedstate";

Vue.use(Vuex);

export default new Vuex.Store({
	//https://stackoverflow.com/questions/52591795/vuex-persisted-state-doesnt-remove-state-after-closing-tab
	plugins: [createPersistedState({
		storage: window.sessionStorage,
		rehydrated(store) {
			
			if (store.state.jwt && store.state.jwt.token) {
				//Modify the axios header to add back the bearer token
				axios.defaults.headers.common['Authorization'] = `Bearer ${store.state.jwt.token}`;
			}

			//Date is not rehydrated as a date object when refreshing, so ensure it is a date
			for (var audit of store.state.audits) {
				audit.date = new Date(audit.date);
			};
			for (var source of store.state.sources) {
				source.date = new Date(source.date);
			};
		}
	})],
	state: {
		authenticated: false,
		redirect: null,
		jwt: null,
		originalJwt: null,
		domains: [],
		emails: [],
		audits: [],
		sources: [],
		user: {},
		modals: {
			logout: {visible:false}
		}
	},
	getters: {
		getDomain: (state) => (name) => {
			for (var domain of state.domains) {
				if (domain.value === name) return domain;
			}
		},
		getDomains: (state) => {
			return state.domains;
		},
		getAudit: (state) => (rowKey) => {
			for (var audit of state.audits) {
				if (audit.rowKey === rowKey) return audit;
			}
		},
		getSource: (state) => (rowKey) => {
			for (var source of state.sources) {
				if (source.rowKey === rowKey) return source;
			}
		},
		siteName: (state) => {
			//If not logged in
			if (!state.user.site) 
			{
				//Determine from host
				if (window.location.hostname.includes('authlogicsstatic.z35.web.core.windows.net')) return 'ey';
				return 'default';
			}

			if (state.user.site === 4) return 'ey';
			return 'default';
		}
	},
	mutations: {
		login (state, data) {
			
			//Force the date on the data
			data.expires = new Date(data.expires);
			
			state.authenticated = true;
			state.jwt = data;

			//Modify the axios header to add the bearer token
			axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
		},
		logout (state) {

			state.authenticated = false;
			state.originalJwt = null;
			state.jwt = null;
			state.expires = null;
			state.domains = [];
			state.emails = [];
			state.audits = [];
			state.sources = [];
			state.user = {};

			//Remove the bearer token
			axios.defaults.headers.common['Authorization'] = null;
		},
		switch (state, data) {
			
			if (!state.originalJwt) state.originalJwt = state.jwt;
			state.jwt = data;

			state.domains = [];
			state.emails = [];
			state.audits = [];
			state.sources = [];
			state.user = {};

			//Change the bearer token
			axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
		},
		unswitch (state) {
			state.jwt = state.originalJwt;
			state.originalJwt = null;

			state.domains = [];
			state.emails = [];
			state.audits = [];
			state.sources = [];
			state.user = {};

			//Change the bearer token back
			axios.defaults.headers.common['Authorization'] = `Bearer ${state.jwt.token}`;
		},
		setDomains (state, domains) {
			state.domains = domains;
		},
		setEmails (state, emails) {
			state.emails = emails;
		},
		setAudits (state, audits) {
			state.audits = audits;
		},
		setSources (state, sources) {
			state.sources = sources;
		},
		setUser (state, user) {
			state.user = user;
		},
		showModal(state, value) {
			const modal =  value.charAt(0).toLowerCase() + value.slice(1);
			state.modals[modal].visible = true;
		},
		hideModal(state, value) {
			const modal =  value.charAt(0).toLowerCase() + value.slice(1);
			state.modals[modal].visible = false;
		},
		setDomainNotificationCount(state, values) {
			values.domain.notificationCount = values.count;
		},
		setDomainSourceTotal(state, values) {
			values.domain.sourceTotal = values.sourceTotal;
		},
		setDomainSourceAdditions(state, values) {
			values.domain.sourceAdditions = values.sourceAdditions;
		},
		updateDomainStatus(state, values) {

			for (var existing of state.domains) {
				if (existing.value === values.value) {

					//Copy certain values across into the domain object
					existing.statusDescription = values.statusDescription
					existing.statusDetail = values.statusDetail;
				}
			}
		},
		setRedirect(state, value) {
			state.redirect = value;
		},
		clearRedirect(state) {
			state.redirect = null;
		}
	},
	actions: {
		async getDomainsAsync ({ commit, dispatch }) {
			const response = await api.get('domain');	
			const statusLookup = ['Unknown', 'Queued', 'Processing', 'Completed', 'Failed', 'Aborted'];
			const industryLookup = {
				10:"Energy",
				15:"Materials",
				20:"Industrials",
				25:"Consumer Discretionary",
				30:"Consumer Staples",
				35:"Health Care",
				40:"Financials",
				45:"Information Technology",
				50:"Telecommunication Services",
				55:"Utilities",
				60:"Real Estate"
			}
			
			for (var domain of response.data) {			
				domain.statusDescription = statusLookup[domain.queueStatus];
				domain.industryDescription = industryLookup[domain.industry];

				if (domain.statusDescription === 'Completed') 
				{
					domain.statusDetail = `${domain.totalBreached} credentials`;
				}
				else if (domain.statusDescription === 'Processing')
				{
					domain.statusDetail = `(Processing ${domain.percentageCompleted} %)`;
				}
				else {
					domain.statusDetail = `(${domain.statusDescription})`;
				}
				domain.notificationCount = null;
			}

			commit('setDomains', response.data);

			//Now, we want to start fetching all the notifications
			dispatch('getDomainNotifications');
		},
		async refreshDomainsAsync ({ commit, dispatch }) {
			const response = await api.get('domain');	
			const statusLookup = ['Unknown', 'Queued', 'Processing', 'Completed', 'Failed', 'Aborted'];

			for (var domain of response.data) {	
				domain.statusDescription = statusLookup[domain.queueStatus];

				if (domain.statusDescription === 'Completed') 
				{
					domain.statusDetail = `${domain.totalBreached} credentials`;
				}
				else if (domain.statusDescription === 'Processing')
				{
					domain.statusDetail = `(Processing ${domain.percentageCompleted} %)`;
				}
				else {
					domain.statusDetail = `(${domain.statusDescription})`;
				}

				//Get matching domain and update
				commit('updateDomainStatus', domain);
			}
		},
		async getDomainNotifications ({ commit, state }) {
			for (var domain of state.domains) {
				
				try
				{
					const response = await api.get(`notifications/${domain.value}/count`);	
					const count = response.data.item1;

					//Check for any data
					if (count > 0) commit('setDomainNotificationCount', {domain, count});
				}
				catch(err) {

				}
			}
		},
		async getEmailsAsync ({ commit }) {
			const response = await api.get('email');	
			const statusLookup = ['Unknown', 'Queued', 'Processing', 'Completed', 'Failed', 'Aborted'];
			
			for (var email of response.data) {			
				email.statusDescription = statusLookup[email.queueStatus];

				if (email.statusDescription === 'Completed') 
				{
					email.statusDetail = `${email.score} credentials`;
				}
				else if (email.statusDescription === 'Processing')
				{
					email.statusDetail = `(Processing)`;
				}
				else {
					email.statusDetail = `(${email.statusDescription})`;
				}
			}

			commit('setEmails', response.data);
		},
		async getAuditsAsync ({ commit }) {
			const response = await api.get('audit');	

			//Loop through all data and work out date
			for (var result of response.data) {
				const rk = result.rowKey;
				result.date = new Date(`${rk.substring(0,4)}-${rk.substring(4,6)}-${rk.substring(6,8)}`); //201809171517058231
			}

			//Sort all the data
			response.data.sort((a,b) => b.date - a.date);
	
			commit('setAudits', response.data);
		},
		async getSourcesAsync ({ commit }) {
			const response = await api.get('system/sources');	

			//Loop through all data and work out date
			for (var result of response.data) {
				result.date = new Date(result.date); //201809171517058231
			}

			//Sort all the data
			response.data.sort((a,b) => b.date - a.date);

			commit('setSources', response.data);
		},
		async getUserAsync ({ commit }) {
			const response = await api.get('token/user');	
			
			commit('setUser', response.data);
		}
	}
});


// WEBPACK FOOTER //
// ./src/state/index.js